import React, { useEffect, useState, useContext, useRef } from "react";
import { loadElementScript } from '../loadConnatixScript';
import { AdsContext } from '@buzzfeed/adlib/dist/module/bindings/react/contexts';
import { getPageSessionId } from '@buzzfeed/adlib/dist/module/services/page-targeting';
import { CID, getPlayerIds } from '@buzzfeed/react-components/lib/utils/connatix';
import CommentsContext from '../../../contexts/comments';
import { isNews } from '../../../utils/isNews';
import { isOrHasQuiz } from '../../../utils/isOrHasQuiz';
import { isGamesPost } from '../../../utils/games';
import { isBackfillEligible, isStickyVideoEligible, hasConnatixVideo, isShoppingSdxaBackfill } from '../../../utils/connatixVideo';
import { useFloatingMode } from '../useFloatingMode';
import { isMobile } from '@buzzfeed/bf-utils/lib/device';
import styles from './elements.module.scss';
import { rules } from '@buzzfeed/consent';
import ABeagleContext from '../../../contexts/abeagle';
import {
  CARD_STYLE_EXPERIMENT,
  isCardStyleSuppressedVariant,
} from '../../../utils/cardStyleQuizExperiment';
import { isIDNML } from '../../../utils/idnml';
import ExCoPlayer from '../../ExCoPlayer';

// Shopping backfill player: origin contains sdxa
const SHOPPING_SDXA_PLAYER_ID = '23bdb39b-81e1-4a9b-84e8-f6e050016b8d';

export default function ElementsPlayer({ buzz = {}, showOnTopQuiz = false }) {
  const [cnxPixel, setCnxPixel] = useState(null);
  const adsContext = useContext(AdsContext);
  const [playerApi, setPlayerApi] = useState(null);
  const [pageSessionId, setPageSessionId] = useState(null);
  const containerId = useRef(null);
  const playerWrapper = useRef(null);
  const hasInitialized = useRef(false);
  const { isCommentsPanelOpen } = useContext(CommentsContext);
  const { getFeatureFlagValue, getExperimentValue, experiments } = useContext(ABeagleContext);

  const isExperimentsLoaded = experiments && experiments.loaded;
  const isBackfillPlayerEnabled = isExperimentsLoaded
    ? getFeatureFlagValue('Ads-Connatix-Backfill-Player')
    : false;
  const isDesktopFloatingPlayerEnabled = isExperimentsLoaded
    ? getExperimentValue('RT-2332-sticky-connatix-desktop-bf', { rejectErrors: false }) === 'on'
    : false;
  const cardStyleVariant = isExperimentsLoaded
    ? getExperimentValue(CARD_STYLE_EXPERIMENT, { rejectErrors: false })
    : null;

  const isDesktopFloatingPlayerEnabledOnQuizAndGames = isExperimentsLoaded
    ? getExperimentValue('RT-2424-sticky-connatix-desktop-games', { rejectErrors: false }) === 'on'
    : false;

  // Connatix script is loaded from the head
  // change connatix player based on user country
  // specifically for AU/NZ, and Japan
  let userCountry = adsContext?.env?.userCountry;

  // Don't show backfill players on US mobile quizzes (resolved from ads_quiz_experience test)
  const isUSMobileQuiz = userCountry === 'us' && isMobile() && buzz.is_quiz;
  const isSuppressedForCardStyleVariant = isCardStyleSuppressedVariant(cardStyleVariant);

  const showElements =
    isBackfillPlayerEnabled &&
    !isSuppressedForCardStyleVariant &&
    !isUSMobileQuiz &&
    (isBackfillEligible(buzz) || (!hasConnatixVideo(buzz) && showOnTopQuiz));

  // For IDNML users, use Ex.Co instead of Connatix
  const isExCoRegion = isIDNML({ isBFN: isNews(buzz) });

  let gdpr = adsContext.gdpr;
  const destination = isNews(buzz) ? 'buzzfeed_news' : 'buzzfeed';

  let playerId, embedId;
  if (isShoppingSdxaBackfill(buzz)) {
    playerId = SHOPPING_SDXA_PLAYER_ID;
    embedId = SHOPPING_SDXA_PLAYER_ID;
  } else {
    const playerIds = getPlayerIds('backfill', destination, showOnTopQuiz);
    playerId = playerIds.playerId;
    embedId = playerIds.embedId;
  }

  let scriptId = `player-${embedId}`;

  useEffect(() => {
    if (isExCoRegion) return;
    const existingScript = document.querySelector(`script[src*="connatix.player.js"]`);
    if (!existingScript) {
      loadElementScript();
    }
  }, [isExCoRegion]);

  useEffect(() => {
    if (!gdpr) {
      return;
    }

    getPageSessionId({gdpr}).then(psi => {
      setPageSessionId(psi);
    });
  }, [gdpr]);

  useEffect(() => {
    // rendering player before userCountry can result in duplicate players in JP
    if (isExCoRegion || !showElements || !userCountry || hasInitialized.current || !pageSessionId) {
      return;
    }
    hasInitialized.current = true;

    if (!cnxPixel) {
      const pixelUrl = `https://capi.connatix.com/tr/si?token=${playerId}&cid=${CID}`;
      const pixelImg = new Image();
      pixelImg.src = pixelUrl;
      setCnxPixel(pixelImg);
    }

    rules.hasConsented('connatix').then(hasConsent => {
      window.cnx.cmd.push(() => {
        const cnxEmbedSettings = {
          playerId,
          customParam1: pageSessionId,
          settings: {
            disableAdvertising: !hasConsent,
            rendering: {
              insertPosition: window.cnx.configEnums.InsertPositionEnum.WithinContainer,
            }
          }
        };

        if (isDesktopFloatingPlayerEnabled) {
          cnxEmbedSettings.settings.customization = {
            floating: {
              mode: 2, // enable floating for sticky desktop AB test variant AfterInView
              closeButton: 1, // AlwaysOn
              floatingGutterY: 54, // height of bottom sticky ad
            }
          };
        }

        if (isDesktopFloatingPlayerEnabledOnQuizAndGames) {
          cnxEmbedSettings.settings.customization = {
            floating: {
              mode: isGamesPost(buzz) ? 3 : 2, // always sticky for games and AfterInView for quizzes
              closeButton: 1, // AlwaysOn
              floatingGutterY: 54, // height of bottom sticky ad
            }
          };
        }

        // TOFIX: embedId =/= mediaId, but was set as such
        if(['uk', 'gb', 'ie'].includes(userCountry)) {
          cnxEmbedSettings.playlistId = embedId;
        }

        const player = window.cnx(cnxEmbedSettings);
        player.render(scriptId, (renderError, api) => {
          if (renderError) {
            console.error('Player rendering failed:', renderError);
            return;
          }
          setPlayerApi(api);
        });
      });
    });
  }, [buzz, showElements, userCountry, playerId, scriptId, cnxPixel, pageSessionId, embedId, isDesktopFloatingPlayerEnabled, isDesktopFloatingPlayerEnabledOnQuizAndGames]);

  const shouldEnableFloating = (
    playerApi &&
    (window.innerWidth <= 1025 || isDesktopFloatingPlayerEnabled) &&
    isStickyVideoEligible(buzz) &&
    !isCommentsPanelOpen &&
    !isOrHasQuiz(buzz) &&
    !isGamesPost(buzz)
  ) || (
    playerApi &&
    isOrHasQuiz(buzz) &&
    isDesktopFloatingPlayerEnabledOnQuizAndGames
  ) || false;
  useFloatingMode(playerApi, playerWrapper, shouldEnableFloating);

  // For IDNML users, render Ex.Co player instead of Connatix
  if (showElements && isExCoRegion) {
    return <ExCoPlayer />;
  }

  if (!showElements || !userCountry) {
    return '';
  }

  const classModifier = isGamesPost(buzz) ? styles.bfElementsWrapperGames : (
    showOnTopQuiz ? styles.bfElementsWrapperQuiz : styles.bfElementsWrapper
  );

  return (
    <div className={`bf-cnx-override ${classModifier}`} ref={playerWrapper}>
      <div id={scriptId} ref={containerId}
        className={`bf-cnx-player ${shouldEnableFloating ? '' : styles.floatingDisabled}`}></div>
    </div>
  );
}
